import {
  MessageBotEdge,
  MessageBotEdgeTypesEnum,
  MessageBotNode,
  MessageBotNodesEnum,
} from "@lib/types/MessageBot";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { EdgeChange, NodeChange } from "@xyflow/react";

type Props = {
  nodes: MessageBotNode[];
  selectedNode: MessageBotNode | null;
  edges: MessageBotEdge[];
  selectedEdge: MessageBotEdge | null;
  addNodeModal: {
    isOpen: boolean;
    parentId: string | null;
  };
};

const initialState: Props = {
  nodes: [
    {
      id: "root",
      generation: 0,
      type: MessageBotNodesEnum.ROOT,
      position: { x: 0, y: 0 },
      data: { label: "ROOT" },
    },
  ],
  selectedNode: null,
  edges: [],
  selectedEdge: null,
  addNodeModal: {
    isOpen: false,
    parentId: null,
  },
};

const slice = createSlice({
  name: "messageBot",
  initialState: initialState,
  reducers: {
    addNode: (
      state,
      action: PayloadAction<MessageBotNode & { parent?: MessageBotNode }>,
    ) => {
      const { parent, ...newNode } = action.payload;
      newNode.id = `${parent?.id || "root"}->${newNode.id}`;
      newNode.generation = (parent?.generation || 0) + 1;
      newNode.parentId = parent?.id || null;

      // Add node to state
      state.nodes.push(newNode);

      // Add edge to state
      state.edges.push({
        id: `${parent?.id || "root"}->${newNode.id}`,
        source: parent?.id || "root",
        target: newNode.id,
        type: MessageBotEdgeTypesEnum.Dashed,
      });

      // Recalculate positions for Generation 1 nodes
      if (newNode.generation === 1) {
        recalculateGen1Positions(state.nodes);
      } else if (newNode.generation >= 2 && parent) {
        // Position based on parent direction for Generation 2+
        const siblings = state.nodes.filter((node) =>
          state.edges.some(
            (edge) => edge.source === parent.id && edge.target === node.id,
          ),
        );

        // Set position for the new node
        const nodeIndex = state.nodes.findIndex(
          (node) => node.id === newNode.id,
        );
        if (nodeIndex !== -1) {
          state.nodes[nodeIndex].position = calculateGen2PlusPosition(
            state.nodes,
            state.edges,
            parent.id,
            newNode.id,
            siblings,
          );
        }

        // Update sibling positions
        updateChildrenPositions(state.nodes, state.edges, parent.id);
      }
    },
    setNodes: (state, action: PayloadAction<NodeChange[]>) => {
      const changes = action.payload;
      changes.forEach((change) => {
        switch (change.type) {
          case "position": {
            const node = state.nodes.find((node) => node.id === change.id);
            if (node) {
              node.position = change.position;
            }
            break;
          }
          case "select": {
            if (!change.selected) {
              state.selectedNode = null;
              state.nodes.forEach((node) => {
                node.selected = false;
              });
            } else {
              state.selectedNode = state.nodes.find(
                (node) => node.id === change.id,
              );
              state.nodes.forEach((node) => {
                node.selected = node.id === change.id;
              });
            }
            break;
          }
        }
      });
    },
    deleteNode: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const index = state.nodes.findIndex((node) => node.id === id);
      if (index !== -1) {
        state.nodes.splice(index, 1);
      }
    },
    addEdge: (state, action: PayloadAction<MessageBotEdge>) => {
      const { source, target } = action.payload;
      const sourceNode = state.nodes.find((node) => node.id === source);
      const targetNode = state.nodes.find((node) => node.id === target);
      if (sourceNode && targetNode) {
        state.edges.push(action.payload);
      }
    },
    setEdges: (state, action: PayloadAction<EdgeChange[]>) => {
      const changes = action.payload;
      changes.forEach((change) => {
        switch (change.type) {
          case "remove": {
            const index = state.edges.findIndex(
              (edge) => edge.id === change.id,
            );
            if (index !== -1) {
              state.edges.splice(index, 1);
            }
            break;
          }
          case "add": {
            // state.edges.push({
            //   id: `${parent?.id || "root"}->${change.target}`,
            //   source: parent?.id || "root",
            //   target: newNode.id,
            //   type: MessageBotEdgeTypesEnum.Dashed,
            // });
            break;
          }
          default: {
            break;
          }
        }
      });
    },
    setAddNodeModal: (
      state,
      action: PayloadAction<{ isOpen: boolean; parentId: string } | null>,
    ) => {
      if (action.payload) {
        state.addNodeModal = action.payload;
      } else {
        state.addNodeModal = {
          isOpen: false,
          parentId: null,
        };
      }
    },
  },
});

export default slice.reducer;
export const {
  addNode,
  setNodes,
  deleteNode,
  addEdge,
  setEdges,
  setAddNodeModal,
} = slice.actions;

const GEN1_RADIUS = 350;
const GEN2_DISTANCE = 100;
const GEN2_SIBLING_DISTANCE = 200;

// Calculate position for Generation 1 nodes
const calculateGen1Position = (index: number, totalChildren: number) => {
  const radius = GEN1_RADIUS;
  const angleStep = (2 * Math.PI) / totalChildren;
  const angle = index * angleStep;

  return {
    x: radius * Math.cos(angle),
    y: radius * Math.sin(angle),
  };
};

// Recalculate positions of Generation 1 nodes
const recalculateGen1Positions = (nodes: MessageBotNode[]) => {
  const gen1Nodes = nodes.filter((node) => node.generation === 1);
  const totalGen1 = gen1Nodes.length;

  if (totalGen1 > 0) {
    gen1Nodes.forEach((node, index) => {
      const nodeIndex = nodes.findIndex((n) => n.id === node.id);
      if (nodeIndex !== -1) {
        nodes[nodeIndex].position = calculateGen1Position(index, totalGen1);
      }
    });
  }
};

// Calculate position for Generation 2+ nodes
const calculateGen2PlusPosition = (
  nodes: MessageBotNode[],
  edges: MessageBotEdge[],
  parentId: string,
  childId: string,
  siblings: MessageBotNode[],
) => {
  // Find parent node
  const parentNode = nodes.find((node) => node.id === parentId);
  // Find parent's parent
  const parentParentId = edges.find((edge) => edge.target === parentId)?.source;
  const parentParentNode = nodes.find((node) => node.id === parentParentId);

  if (!parentNode || !parentParentNode) {
    return { x: 0, y: 0 };
  }

  // Calculate parent direction
  const direction = {
    x: parentNode.position.x - parentParentNode.position.x,
    y: parentNode.position.y - parentParentNode.position.y,
  };

  // Normalize direction
  const magnitude = Math.sqrt(
    direction.x * direction.x + direction.y * direction.y,
  );
  const normalizedDirection = {
    x: direction.x / magnitude,
    y: direction.y / magnitude,
  };

  // Distance between nodes
  const distance = GEN2_DISTANCE;

  // Calculate position based on main direction
  const basePosition = {
    x: parentNode.position.x + normalizedDirection.x * distance,
    y: parentNode.position.y + normalizedDirection.y * distance,
  };

  // If no siblings, use the base position directly
  if (siblings.length <= 1) {
    return basePosition;
  }

  // Calculate sibling positions perpendicular to main direction
  const perpendicularDirection = {
    x: -normalizedDirection.y,
    y: normalizedDirection.x,
  };

  // Find sibling index
  const siblingIndex = siblings.findIndex((node) => node.id === childId);
  const siblingCount = siblings.length;

  // Total width of siblings
  const totalWidth = (siblingCount - 1) * GEN2_SIBLING_DISTANCE;

  // Calculate sibling position
  const offset = siblingIndex * GEN2_SIBLING_DISTANCE - totalWidth / 2;

  return {
    x: basePosition.x + perpendicularDirection.x * offset,
    y: basePosition.y + perpendicularDirection.y * offset,
  };
};

// Update positions of all children in the tree structure
const updateChildrenPositions = (
  nodes: MessageBotNode[],
  edges: MessageBotEdge[],
  parentId: string,
) => {
  // Find direct children of the node
  const childrenEdges = edges.filter((edge) => edge.source === parentId);
  const childrenIds = childrenEdges.map((edge) => edge.target);
  const children = nodes.filter((node) => childrenIds.includes(node.id));

  // Update position for each child and their children
  children.forEach((child) => {
    // Only update generation 2 and above
    if (child.generation >= 2) {
      const siblings = nodes.filter((node) =>
        edges.some(
          (edge) => edge.source === parentId && edge.target === node.id,
        ),
      );

      // Calculate and update position
      const newPosition = calculateGen2PlusPosition(
        nodes,
        edges,
        parentId,
        child.id,
        siblings,
      );
      const nodeIndex = nodes.findIndex((node) => node.id === child.id);
      if (nodeIndex !== -1) {
        nodes[nodeIndex].position = newPosition;
      }

      // Update children's children as well
      updateChildrenPositions(nodes, edges, child.id);
    }
  });
};
